import Phaser from 'phaser';

/**
 * Reconfigure the Phaser Scale Manager for the new consumer's dimensions
 * and scale mode.  Sets canvas CSS and schedules a refresh so Phaser
 * computes the correct FIT/NONE display size.
 *
 * Returns a cleanup function that cancels any pending refresh.
 * The caller should invoke this on unmount.
 */
export function reconfigureScale(
  game: Phaser.Game,
  canvas: HTMLCanvasElement,
  container: HTMLElement,
  width: number,
  height: number,
  scaling?: number,
  crisp?: boolean,
): () => void {
  const dpr = window.devicePixelRatio || 1;
  const scaleMode = scaling ?? Phaser.Scale.FIT;
  const gameWidth = crisp ? width * dpr : width;
  const gameHeight = crisp ? height * dpr : height;

  // Update the scale manager's parent reference to the new container.
  const scaleMgr = game.scale as unknown as Record<string, unknown>;
  scaleMgr.parent = container;

  // scaleMode is readonly in the TS defs but writable at runtime
  (game.scale as { scaleMode: number }).scaleMode = scaleMode;
  game.scale.resize(gameWidth, gameHeight);

  if (scaleMode === Phaser.Scale.NONE) {
    // NONE + crisp: canvas resolution is DPR-scaled, CSS is viewport size
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    return () => {};
  }

  // FIT / other modes: schedule a refresh so Phaser re-measures the
  // container (which now has explicit width/height: 100% and fills its
  // parent independently of the canvas) and computes proper fitted
  // dimensions.
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  const rafId = requestAnimationFrame(() => {
    game.scale.refresh();
  });

  return () => {
    cancelAnimationFrame(rafId);
  };
}
